import * as Sentry from "@sentry/react";

/**
 * The messages the purge paths report with. Hoisted and exported so a test can
 * hold them against the Sentry beforeSend ignore patterns: a reworded message
 * that happens to match one (e.g. "Timeout ..." instead of "Timed out ...")
 * would silently restore the very silence this reporter exists to prevent.
 */
export const DEFAULTS_PURGE_FAILURE_MESSAGES = {
  treeCollectFailed:
    "Failed to collect the project's shapefiles before deletion; " +
    "its fields will not be purged from user defaults",
  shapefilePurgeFailed: "Failed to purge deleted shapefile fields from user defaults",
  fieldPurgeFailed: "Failed to purge a deleted field from user defaults"
} as const;

/**
 * A purge (or the read that feeds it) that silently doesn't happen recreates
 * the exact failure mode EVA-126 fixes - a stale field stuck in the saved
 * defaults - so every best-effort failure is reported to Sentry as well as
 * the console: nobody reads a production console, and the neighbouring
 * useResolvedDefaultFieldList breadcrumb is how a stuck default gets
 * diagnosed after the fact.
 *
 * captureMessage rather than captureException, deliberately: the likeliest
 * causes here (network failures, timeouts, cancellations) match the global
 * beforeSend ignore patterns, so an exception event would be silently dropped
 * - inverting this helper's purpose. The message text passes the filter and
 * the underlying error rides along as context.
 *
 * Never throws: callers include react-query v3 onMutate handlers (where a
 * rejection aborts the mutation itself) and the module-level purge queue
 * (where a rejected link would kill the queue for the page's lifetime), so a
 * reporting failure must never outrank the flow being reported on.
 *
 * Imported directly (not via the hooks barrel) so tests that mock the barrel
 * for the purge hook keep this reporting real.
 */
export function reportDefaultsPurgeFailure(message: string, error?: unknown): void {
  try {
    if (error === undefined) {
      // eslint-disable-next-line no-console
      console.error(message);
    } else {
      // eslint-disable-next-line no-console
      console.error(message, error);
    }
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setContext("defaultsPurge", {
        message,
        error: error === undefined ? null : String(error)
      });
      Sentry.captureMessage(message);
    });
  } catch {
    // Deliberately swallowed - see the docstring. There is no further
    // reporting channel to hand this to.
  }
}
